import React from 'react';
import { Skeleton } from "@/shared/components";
import SettingsPage from '@/pages/SettingsPage';
import RepositoriesTab from '@/features/dashboard/components/RepositoriesTab';
import RecentScans from '@/features/dashboard/components/RecentScans';
import SingleRepoSelector from '@/features/dashboard/components/SingleRepoSelector';
import { useDashboard } from '../hooks/useDashboard';
import { ConnectRepositoryAlert } from '../components/DashboardAlerts';
import { DashboardLayout } from '../components/DashboardLayout';
import { Section } from '@/ui/layout/Section';
import { SheetBadge } from '@/ui/layout/SheetBadge';
import { Button } from '@/ui/Button';
import { Rocket } from 'lucide-react';

const DashboardPage: React.FC = () => {
  const {
    userData,
    loading,
    error,
    repositories,
    recentReviews,
    selectedRepoId,
    setSelectedRepoId,
    installationStatus,
    activeTab,
    handleTabChange
  } = useDashboard();

  if (error) {
    return (
       <DashboardLayout activeTab={activeTab} onTabChange={handleTabChange}>
         <Section className="p-12">
            <div className="p-4 border-2 border-red-500 bg-red-50 text-red-900 font-mono">
                <h3 className="font-bold text-lg mb-2">SYSTEM ERROR</h3>
                <p>{error}</p>
            </div>
         </Section>
       </DashboardLayout>
    );
  }

  const checkoutUrl = userData?.id
    ? `https://codiidev.lemonsqueezy.com/buy/77b9e783-f8ab-4778-a05c-ecc78576ed83?checkout[custom][user_id]=${userData.id}`
    : `https://codiidev.lemonsqueezy.com/buy/77b9e783-f8ab-4778-a05c-ecc78576ed83`;

  return (
    <DashboardLayout activeTab={activeTab} onTabChange={handleTabChange}>
          {loading ? (
             <Section className="p-12">
                 <Skeleton className="h-8 w-[200px] mb-4 rounded-none" />
                 <Skeleton className="h-4 w-[300px] rounded-none" />
             </Section>
          ) : (
            <>
               {/* Welcome / Header Section for Content */}
               <Section className="p-8 md:p-12" label="USER_CONTEXT">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                      <div>
                          <SheetBadge className="mb-4">DASHBOARD_OVERVIEW</SheetBadge>
                          <h1 className="text-3xl md:text-4xl font-bold font-mono tracking-tight uppercase">
                              Welcome back, {userData?.username || 'Engineer'}
                          </h1>
                          <p className="mt-2 text-muted-foreground font-mono text-sm border-l-2 border-neutral-950 dark:border-neutral-100 pl-3">
                              Here's the latest telemetry from your repositories.
                          </p>
                      </div>

                      {installationStatus?.is_installed && userData?.subscription_tier !== 'dev' && (
                          <Button
                            asChild
                            size="sm"
                            className="rounded-none font-mono font-bold uppercase tracking-wider bg-green-600 hover:bg-green-700 text-white dark:bg-green-500 dark:hover:bg-green-600 border-none shadow-sm"
                          >
                            <a href={checkoutUrl} className="lemonsqueezy-button flex items-center gap-2">
                                <Rocket className="h-4 w-4" />
                                [ UPGRADE_PLAN ]
                            </a>
                          </Button>
                      )}
                  </div>
               </Section>

                {/* Alerts Area - Only if NOT installed */}
                {!installationStatus?.is_installed && (
                     <div className="px-8 md:px-12 py-8">
                        <ConnectRepositoryAlert installationStatus={installationStatus} />
                     </div>
                )}
            </>
          )}

          {/* Main Tab Content */}
          {installationStatus?.is_installed && activeTab === 'overview' && (
            <div>
                 <Section className="px-8 py-8 md:px-12" label="REPOSITORY_CONTROL">
                    <SingleRepoSelector
                        repositories={repositories}
                        selectedRepoId={selectedRepoId}
                        onSelectRepo={setSelectedRepoId}
                    />
                 </Section>

                 <Section className="px-8 py-8 md:px-12" label="RECENT_ACTIVITY">
                     <RecentScans data={recentReviews} loading={loading} />
                 </Section>
            </div>
          )}

          {!loading && installationStatus?.is_installed && activeTab === 'settings' && (
            <Section className="p-8 md:p-12" label="SYSTEM_CONFIG">
                <SettingsPage />
            </Section>
          )}

          {!loading && installationStatus?.is_installed && activeTab === 'repositories' && (
             <div className="space-y-6">
                <RepositoriesTab />
             </div>
          )}
    </DashboardLayout>
  );
};

export default DashboardPage;
